import { BookingRecoveryStorageError } from '../../../../lib/booking-recovery-storage'
import { recoverRetainedBooking, BookingSubmissionClosed } from '../../../../lib/booking-submission-recovery'
import { systemClock } from '@ascleo/types/date-time-validity'
import { useTranslation } from 'react-i18next'
import { errorService } from '@ascleo/client/services/error.service'
import { createBookingQuestionnaireClient } from '../../../../lib/rpc/booking-questionnaire.client'
import { QuestionnaireStep, QuestionnaireLoadingStep, QuestionnaireErrorStep } from './steps/QuestionnaireStep'
import { useCallback, useEffect, useEffectEvent, useRef, useState } from 'react'
import { useMachine, useSelector } from '@xstate/react'
import { schedulerMachine, type SchedulerStateId, type Insurance } from './machine'
import { SchedulerActorProvider } from './context'
import { docCirrusRpcClient } from '../../../../lib/rpc/doc-cirrus.client'
import { frontdeskDiagnosticLogger } from '../../../../lib/diagnostic-logger'
import { useFlowBackHandler, useOptionalSelfService } from '../../context'
import { KioskStep } from '../../../../components/kiosk-step'
import { KioskLoadingPanel } from '../../../../components/kiosk-state-panel'
import { flowPhaseProgress, usePublishFlowPhaseProgress, type FlowPhasePlacement } from './flow-phase-progress'
import { PatientIdentificationStep } from './steps/PatientIdentificationStep'
import { AppointmentTypeStep } from './steps/AppointmentTypeStep'
import { SlotSelectionStep } from './steps/SlotSelectionStep'
import { ConfirmationStep } from './steps/ConfirmationStep'
import { SchedulerDoneStep } from './steps/SchedulerDoneStep'
import { SchedulerErrorStep } from './steps/SchedulerErrorStep'

/** First state of the scheduler flow - back from here exits the flow */
const FIRST_STATE: SchedulerStateId = 'patientIdentification'

/**
 * Compile-time proof that every scheduler state has a composition above.
 *
 * The parameter is `never`, so adding a state to the machine without adding a
 * case here fails the type check. At runtime it renders nothing rather than a
 * raw state name — a patient must never read internal identifiers.
 */
function assertNoUnrenderedSchedulerState(state: never): null {
  frontdeskDiagnosticLogger.error(
    '[Scheduler] no composition for machine state; rendering nothing',
    { state }
  )
  return null
}

/**
 * Where each scheduler state sits in the three named phases
 * "Anmelden · Termin wählen · Bestätigen" (KO-20). Questionnaire, type and
 * slot choice are sub-steps of the second phase; outcome screens hide the
 * stepper, a confirmed booking fills it.
 */
function schedulerPhasePlacement(state: SchedulerStateId): FlowPhasePlacement {
  switch (state) {
    case 'patientIdentification':
      return { phase: 1 }
    case 'recoveryLoading':
    case 'questionnaireLoading':
    case 'questionnaire':
    case 'routeLoading':
    case 'appointmentType':
      return { phase: 2, subStep: { current: 1, total: 2 } }
    case 'slotSelection':
      return { phase: 2, subStep: { current: 2, total: 2 } }
    case 'confirmation':
    case 'booking':
      return { phase: 3 }
    case 'done':
      return { kind: 'complete' }
    case 'questionnaireError':
    case 'recoveryError':
    case 'error':
      return { kind: 'hidden' }
  }
}

/** Outcome screens own their exits; the footer "Zurück" is hidden there (KO-05). */
const SCHEDULER_TERMINAL_STATES: ReadonlySet<SchedulerStateId> = new Set(['done', 'error', 'recoveryError'])

interface SchedulerFlowProps {
  onComplete?: () => void
  /**
   * When entering the scheduler from a no-appointment check-in, the
   * already-identified patient to reuse. The flow fetches the patient's
   * demographics once and skips identification straight to appointment
   * selection. Omit for a normal hub entry (always a fresh identification).
   */
  initialPatient?: { id: string; insurance?: Insurance }
}

/**
 * Scheduler flow orchestrator component.
 * Uses XState to manage the appointment booking process.
 *
 * Security: The machine instance is created fresh on mount and destroyed on unmount.
 * Patient data is isolated per session - when the flow ends, the machine is garbage collected.
 */
export function SchedulerFlow({ onComplete, initialPatient }: SchedulerFlowProps) {
  const [, send, actorRef] = useMachine(schedulerMachine)
  const { t, i18n } = useTranslation()
  const [questionnaireClient] = useState(() => createBookingQuestionnaireClient())
  const patientId = useSelector(actorRef, (s) => s.context.patient?.id)
  const projectedLanguage = useSelector(actorRef, (s) => s.context.questionnaireLanguage)
  const questionnaireCommand = useSelector(actorRef, (s) => s.context.questionnaireCommand)
  const language = (i18n.resolvedLanguage || i18n.language || 'de').split('-')[0] ?? 'de'


  const currentState = useSelector(actorRef, (s) => s.value as SchedulerStateId)

  // Diagnostics read the current translation without making `t` an effect
  // dependency: a session-language change must not restart (and so repeat) an
  // in-flight recovery or questionnaire operation.
  const reportRecoveryUnavailable = useEffectEvent(function reportRecoveryUnavailable() {
    // The step renders the outcome; the patient never sees this diagnostic (KO-01, KO-02).
    errorService.report(new Error('Booking status recovery unavailable'), { layer: 'rpc', source: 'frontdesk.scheduler.recovery', userMessage: t('self-service.scheduler.error.pending-info'), suppressToast: true })
  })
  const reportQuestionnaireFailure = useEffectEvent(function reportQuestionnaireFailure() {
    errorService.report(new Error('Booking questionnaire operation failed'), { layer: 'rpc', source: 'frontdesk.scheduler.questionnaire', userMessage: t('self-service.scheduler.questionnaire.error-title'), suppressToast: true })
  })

  useEffect(function recoverAfterPatientIdentification() {
    if (currentState !== 'recoveryLoading') return
    let active = true
    const currentPatientId = () => active && actorRef.getSnapshot().matches('recoveryLoading') ? actorRef.getSnapshot().context.patient?.id : undefined
    void (async () => {
      try {
        if (patientId === undefined) throw new Error('Patient identification is required')
        const result = await recoverRetainedBooking({ patientId, currentPatientId, storage: globalThis.localStorage, clock: systemClock, status: docCirrusRpcClient.frontdesk.getBookingStatus })
        if (!active || currentPatientId() !== patientId) return
        if (result === undefined) send({ type: 'RECOVERY_NONE' })
        else send({ type: 'BOOKING_SUCCESS', bookingId: result.scheduleId, startTime: result.startTime, endTime: result.endTime })
      } catch (error) {
        if (!active) return
        send({ type: 'BOOKING_ERROR', outcome: error instanceof BookingSubmissionClosed ? error.reason : error instanceof BookingRecoveryStorageError && ['missing', 'expired', 'malformed'].includes(error.reason) ? 'assistance' : 'pending' })
        reportRecoveryUnavailable()
      }
    })()
    return function invalidateEndedRecovery() { active = false }
  }, [currentState, patientId, actorRef, send])

  const priorQuestionnaireLanguage = useRef(language)
  useEffect(function reloadQuestionnaireForSessionLanguage() {
    const changed = priorQuestionnaireLanguage.current !== language
    priorQuestionnaireLanguage.current = language
    if (currentState === 'questionnaireLoading' || currentState === 'routeLoading') return
    if (changed || (projectedLanguage !== undefined && projectedLanguage !== language)) send({ type: 'QUESTIONNAIRE_LANGUAGE_CHANGED' })
  }, [language, projectedLanguage, currentState, send])

  useEffect(function runQuestionnaireOperation() {
    if (currentState !== 'questionnaireLoading' && currentState !== 'routeLoading') return
    let active = true
    void (async () => {
      try {
        if (currentState === 'routeLoading') {
          const route = await questionnaireClient.route()
          if (active) send({ type: 'QUESTIONNAIRE_ROUTE', route })
          return
        }
        if (patientId === undefined) throw new Error('Patient identification is required')
        let selectedLanguage = priorQuestionnaireLanguage.current
        let response = questionnaireCommand?.kind === 'answer'
          ? await questionnaireClient.answer(questionnaireCommand.answerId)
          : questionnaireCommand?.kind === 'back'
            ? await questionnaireClient.back()
            : await questionnaireClient.load(patientId, selectedLanguage)
        while (selectedLanguage !== priorQuestionnaireLanguage.current) {
          if (!active) break
          selectedLanguage = priorQuestionnaireLanguage.current
          response = await questionnaireClient.changeLanguage(selectedLanguage)
        }
        if (active) send({ type: 'QUESTIONNAIRE_RESPONSE', response, answerCount: questionnaireClient.path().length, language: selectedLanguage })
      } catch {
        if (!active) return
        send({ type: 'QUESTIONNAIRE_ERROR' })
        reportQuestionnaireFailure()
      }
    })()
    return function ignoreSupersededQuestionnaireOperation() { active = false }
  }, [currentState, questionnaireCommand, patientId, questionnaireClient, send])

  useEffect(function revokeEndedQuestionnaireSession() {
    if (currentState !== 'patientIdentification' && currentState !== 'done') return
    void questionnaireClient.reset().catch(() => {
      errorService.report(new Error('Booking context revocation failed'), { layer: 'rpc', source: 'frontdesk.scheduler.questionnaire.reset', suppressToast: true })
    })
  }, [currentState, questionnaireClient])

  useEffect(function ownQuestionnaireSessionLifetime() {
    return function revokeUnmountedQuestionnaireSession() {
      void questionnaireClient.reset().catch(() => {
        errorService.report(new Error('Booking context revocation failed'), { layer: 'rpc', source: 'frontdesk.scheduler.questionnaire.unmount', suppressToast: true })
      })
    }
  }, [questionnaireClient])

  // Reuse a carried-in patient from a no-appointment check-in: fetch the
  // patient's demographics once, then seed PATIENT_IDENTIFIED so the flow skips
  // identification and opens on appointment selection. A short loader covers the
  // fetch so the card-reader step never flashes. On failure, fall back to manual
  // identification.
  const [isSeeding, setIsSeeding] = useState(Boolean(initialPatient))
  const seededRef = useRef(false)
  useEffect(function seedCarriedPatient() {
    if (!initialPatient || seededRef.current) return
    seededRef.current = true
    void (async () => {
      try {
        const [record] = await docCirrusRpcClient.patient.find({
          _id: initialPatient.id,
        })
        send({
          type: 'PATIENT_IDENTIFIED',
          patient: {
            id: initialPatient.id,
            firstName: record?.firstname ?? '',
            lastName: record?.lastname ?? '',
            dateOfBirth: record?.dob ?? '',
            kvnr: record?.insuranceStatus?.[0]?.insuranceNo,
            insurance: initialPatient.insurance ?? 'GKV',
          },
        })
      } catch (error) {
        frontdeskDiagnosticLogger.error(
          'Failed to load carried-in patient for scheduler; falling back to manual identification',
          { error }
        )
      } finally {
        setIsSeeding(false)
      }
    })()
  }, [initialPatient, send])

  // Publish named-phase progress to the global footer stepper (KO-20).
  const isReceptionHandoff = useSelector(actorRef, (s) => s.matches('questionnaire') && s.context.questionnaire?.kind === 'reception')
  usePublishFlowPhaseProgress(
    isReceptionHandoff
      ? null
      : flowPhaseProgress(
          [t('self-service.scheduler.phase.identify'), t('self-service.scheduler.phase.choose'), t('self-service.scheduler.phase.confirm')],
          schedulerPhasePlacement(currentState),
        ),
  )

  // No footer "Zurück" on outcome screens: a finished or failed booking is
  // left through its own actions only (KO-05).
  const setBackHidden = useOptionalSelfService()?.setBackHidden
  const backHidden = isReceptionHandoff || SCHEDULER_TERMINAL_STATES.has(currentState)
  useEffect(function hideBackOnSchedulerOutcomes() {
    setBackHidden?.(backHidden)
  }, [backHidden, setBackHidden])

  // Register back handler with parent - exit flow if at first state, otherwise go back in flow
  const handleBack = useCallback(() => {
    if (currentState === FIRST_STATE) {
      onComplete?.()
    } else {
      send({ type: 'BACK' })
    }
  }, [currentState, onComplete, send])

  useFlowBackHandler(handleBack)

  const renderStep = () => {
    switch (currentState) {
      case 'recoveryLoading':
      case 'questionnaireLoading':
      case 'routeLoading':
        return <QuestionnaireLoadingStep />

      case 'questionnaire':
        return <QuestionnaireStep />

      case 'questionnaireError':
        return <QuestionnaireErrorStep />

      case 'patientIdentification':
        return <PatientIdentificationStep />

      case 'appointmentType':
        return <AppointmentTypeStep />

      case 'slotSelection':
        return <SlotSelectionStep />

      case 'confirmation':
        return <ConfirmationStep />

      // Submitting keeps the confirmation composition on screen: the step
      // renders its own in-flight state from `snapshot.matches('booking')`,
      // so the patient sees the booking they confirmed with an inert control
      // instead of an unmount.
      case 'booking':
        return <ConfirmationStep />

      case 'done':
        return <SchedulerDoneStep onComplete={onComplete} />

      case 'recoveryError':
      case 'error':
        return <SchedulerErrorStep />
    }

    // Exhaustive: a state added to the machine without a composition here is a
    // type error, not a debug string on a patient-facing kiosk.
    return assertNoUnrenderedSchedulerState(currentState)
  }

  return (
    <SchedulerActorProvider actor={actorRef} questionnaireClient={questionnaireClient} onExit={onComplete}>
      {isSeeding && currentState === 'patientIdentification' ? (
        <KioskStep width="narrow" align="center" title={t('self-service.scheduler.start.title')}>
          <KioskLoadingPanel title={t('self-service.loading')} />
        </KioskStep>
      ) : (
        renderStep()
      )}
    </SchedulerActorProvider>
  )
}
